import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { CompletionCheckbox } from "@/components/CompletionCheckbox";
import { Lock, Info, ExternalLink } from "lucide-react";

interface DetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  isCritical?: boolean;
  children: React.ReactNode;
  testId?: string;
}

export function DetailDialog({
  open,
  onOpenChange,
  title,
  isCritical,
  children,
  testId
}: DetailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCritical && (
              <Lock className="h-5 w-5 text-amber-500" />
            )}
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Details and documents for {title}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6 py-4">
            {children}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

interface CompletionSectionProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
}

export function CompletionSection({
  checked,
  onCheckedChange,
  disabled = false,
  label = "Mark as completed"
}: CompletionSectionProps) {
  return (
    <>
      <div className="flex items-center gap-3">
        <CompletionCheckbox
          id="detail-completed"
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          testId="checkbox-detail-completed"
        />
        <Label htmlFor="detail-completed" className="text-sm font-medium cursor-pointer">
          {label}
        </Label>
      </div>
      <Separator />
    </>
  );
}

type InstructionPart = { type: "text"; content: string } | { type: "link"; content: string; href: string };

function parseInstructions(instructions: string): InstructionPart[] {
  const urlRegex = /(https?:\/\/[^\s<>]+)/g;
  const parts: InstructionPart[] = [];
  let lastIndex = 0;
  let match;
  
  while ((match = urlRegex.exec(instructions)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: instructions.slice(lastIndex, match.index) });
    }
    parts.push({ type: "link", content: match[1], href: match[1] });
    lastIndex = match.index + match[1].length;
  }
  
  if (lastIndex < instructions.length) {
    parts.push({ type: "text", content: instructions.slice(lastIndex) });
  }
  
  return parts;
}

interface InstructionsSectionProps {
  instructions: string;
}

export function InstructionsSection({ instructions }: InstructionsSectionProps) {
  const parts = parseInstructions(instructions);
  
  return (
    <>
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Info className="h-4 w-4 text-blue-500" />
          Instructions
        </Label>
        <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
          {parts.map((part, idx) =>
            part.type === "link" ? (
              <a
                key={idx}
                href={part.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline inline-flex items-center gap-1"
              >
                {part.content}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <span key={idx}>{part.content}</span>
            )
          )}
        </div>
      </div>
      <Separator />
    </>
  );
}
